import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import GlobalButton from "../../components/button";
import ProductCard from "../../components/cards/ProductCard";
import ErrorPage from "../../components/errorPage";
import AppFooter from "../../components/footer";
import NavBar from "../../components/NavBar";
import ProductCardSkeleton from "../../components/skeletons/ProductCardSkeleton";
import { useSnackbar } from "../../lib/context/snackbarContext";
import { getAllProductsByCategoryId } from "../../services/productsFn";
import EcommerceSubCategories from "./SubCategories";
import { useDispatch, useSelector } from "react-redux";
import {
  incrementCategoryPage,
  resetAllCategoryProductsData,
  resetCategoryProducts,
  setCategoryHasMore,
  setCategoryId,
  setCategoryProducts,
  setSubCategoryId,
} from "../../lib/redux/slices/categoryProductSlice";
import { t } from "i18next";

const CategoryProductPage = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  const [isError, setIsError] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const isFirstLoad = useRef(true); // 🔁 Important to track first load

  const queryParams = new URLSearchParams(location.search);
  const sub_category_id = queryParams.get("sub_category");

  const { products, currentPage, limit, hasMore, categoryId, subCategoryId } =
    useSelector((state) => state.categoryProductReducer);

  // 👉 Save scroll position when navigating away
  useEffect(() => {
    return () => {
      sessionStorage.setItem("scrollPosition", window.scrollY);
    };
  }, []);

  // 👉 Restore scroll position
  useEffect(() => {
    const y = sessionStorage.getItem("scrollPosition");
    if (y) {
      window.scrollTo({ top: +y, behavior: "smooth" });
      sessionStorage.removeItem("scrollPosition");
    }
  }, []);

  // ✅ Handle category change
  useEffect(() => {
    if (params.categoryId && params.categoryId !== categoryId) {
      dispatch(resetAllCategoryProductsData());
      dispatch(setCategoryId(params.categoryId));
      setIsLoading(true);
    }
  }, [params.categoryId]);

  // ✅ Handle sub-category change
  useEffect(() => {
    if (sub_category_id && sub_category_id !== subCategoryId) {
      dispatch(resetCategoryProducts());
      dispatch(setSubCategoryId(sub_category_id));
      dispatch(setCategoryHasMore(true));
      setIsLoading(true);
    }
  }, [sub_category_id]);

  // ✅ Fetch data only if not first load
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    if (params.categoryId) {
      fetchAllProducts();
    }
  }, [categoryId, subCategoryId]);

  const fetchAllProducts = async () => {
    try {
      const response = await getAllProductsByCategoryId(
        params.categoryId,
        sub_category_id,
        currentPage,
        limit
      );

      if (response.data.status === "success") {
        const data = response.data.data;
        setCategoryName(data.category.name);
        dispatch(setCategoryProducts(data.products));
        dispatch(setCategoryHasMore(data.products.length >= limit));
      } else {
        setIsError(true);
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      setIsError(true);
      showSnackbar(error.message, "error");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleShowMore = () => {
    dispatch(incrementCategoryPage());
    fetchAllProducts();
    setIsLoadingMore(true);
  };

  if (isError) {
    return <ErrorPage onRetry={fetchAllProducts} />;
  }

  return (
    <>
      <NavBar />
      <Box component={"section"} pt={2} pb={2} sx={{ mt: 11 }}>
        <Box
          sx={{
            position: "sticky",
            top: { xs: "80px", sm: "80px" },
            zIndex: 999,
            backgroundColor: "background.default",
          }}
        >
          <EcommerceSubCategories />
        </Box>

        <Container sx={{ mt: 2.5 }}>
          <Typography
            variant="heading_services"
            component={"h1"}
            textAlign={"center"}
            lineHeight={"normal"}
            fontSize={{ xs: "1.5rem", sm: "2rem" }}
          >
            {t("browseProducts")}{" "}
            <Typography
              variant="heading_services"
              textTransform={"capitalize"}
              fontSize={{ xs: "1.5rem", sm: "2rem" }}
            >
              {categoryName[currentLanguage]}
            </Typography>{" "}
            {t("collection")}!
          </Typography>
        </Container>

        <Container maxWidth={"lg"} sx={{ mt: 3 }}>
          {!isLoading && products.length === 0 && (
            <Typography variant="h4" fontWeight={600} textAlign={"center"}>
              {t("productNotFound")} !!
            </Typography>
          )}

          <Grid container spacing={2}>
            {isLoading
              ? Array.from({ length: 8 }).map((_, index) => (
                  <Grid key={index} item xs={6} sm={4} md={2.4}>
                    <ProductCardSkeleton />
                  </Grid>
                ))
              : products.map((item, index) => (
                  <Grid key={index} item xs={6} sm={4} md={2.4}>
                    <ProductCard cardData={item} colorName={"text.black"} />
                  </Grid>
                ))}
          </Grid>

          <Stack my={4} alignItems={"center"}>
            {hasMore && (
              <GlobalButton
                btnText={"showMore"}
                handleClickBtn={handleShowMore}
                startIcon={isLoadingMore && <CircularProgress size={26} />}
                styleProps={{
                  height: 50,
                  width: 200,
                  backgroundColor: "text.white",
                  color: "text.black",
                  borderRadius: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              />
            )}
          </Stack>
        </Container>
      </Box>

      <AppFooter bgcolor={true} />

      <Chip
        label={t("goBack")}
        onClick={() => navigate(-1)}
        sx={{
          position: "fixed",
          bottom: 15,
          left: 10,
          zIndex: 100000,
          color: "common.white",
          backgroundColor: "primary.main",
          "&:hover": {
            backgroundColor: "primary.main",
          },
        }}
      />
    </>
  );
};

export default CategoryProductPage;
